import type { Schemas } from "@softmax/observatory-client/types";
import type { PublicPlayShell } from "../lib/types";
import { createPlayApi, type PlayApi } from "../data/play-api";
import { boardForViewer, programsForViewer } from "./one-pager-model";
import type { ProgramDetail } from "./programs/program-records";
import { readProgramDetail } from "./program-detail";
import { liveOf } from "./live-state";
import type {
  PageShell,
  OnePagerSlice,
  OnePagerData,
  ProgramDetailInput,
} from "./types";
import { readRecommended } from "./reads/recommended";
import { readPublicBoard } from "./leaderboard/public-board";

async function readOnePagerData({
  api,
  shell,
}: {
  api: PlayApi;
  shell: PageShell;
}): Promise<OnePagerData> {
  const { league, divisionId } = shell;
  const shareable = api.leagueIsPublic(league.id);
  const [leaderboard, rounds, memberships] = await Promise.all([
    divisionId
      ? shareable.then((pub) =>
          api.divisionLeaderboard({
            divisionId,
            anonymous: pub,
          }),
        )
      : null,
    divisionId
      ? shareable.then((pub) =>
          api.rounds({
            divisionId,
            limit: 3,
            anonymous: pub,
          }),
        )
      : null,
    api.get<Schemas["LeaguePolicyMembershipPublic"][]>({
      path: `/v2/league-policy-memberships?mine=true&league_id=${encodeURIComponent(league.id)}`,
    }),
  ]);
  const board = boardForViewer({ shell, leaderboard });
  const programs = programsForViewer({
    memberships,
    board,
  });
  const data: OnePagerSlice = {
    ...board.state,
    yours: programs.yours,
    live: liveOf({
      rounds,
      cadenceMinutes: shell.league.cadenceMinutes,
    }),
  };

  return {
    ...data,
    jsonHref: shell.divisionId
      ? `/api/observatory/v2/divisions/${encodeURIComponent(shell.divisionId)}/leaderboard/summary`
      : null,
  };
}

export function createOnePagerReader({
  signal,
  api = createPlayApi({ signal }),
}: {
  signal?: AbortSignal | undefined;
  api?: PlayApi;
}) {
  return {
    loadOnePager: (shell: PageShell) =>
      readOnePagerData({
        api,
        shell,
      }),
    loadRecommended: (shell: PublicPlayShell) =>
      readRecommended({
        api,
        shell,
      }),
    loadPublicBoard: (shell: PublicPlayShell) =>
      readPublicBoard({
        api,
        shell,
      }),
    loadProgramDetail: (input: ProgramDetailInput) =>
      readProgramDetail({
        signal,
        api,
        input,
      }),
  };
}

export function loadProgramDetail({
  signal,
  input,
}: {
  signal?: AbortSignal | undefined;
  input: ProgramDetailInput;
}): Promise<ProgramDetail> {
  return createOnePagerReader({
    signal,
  }).loadProgramDetail(input);
}
